import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "#components/ui/breadcrumb"
import { Card, CardContent } from "#components/ui/card"
import { InnerSideNav, type InnerSideNavItem } from "#components/ui/inner-side-nav"
import { useI18n } from "#hooks/use-i18n"
import { slideUp } from "#lib/animations"
import { scrollToAnchor } from "#lib/scroll-utils"
import { motion } from "framer-motion"
import { CreditCard, LayoutDashboard, Settings, Tag, UserRound } from "lucide-react"
import { useEffect } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { CategoriesHelp } from "../components/help/help-sections/categories-help"
import { DashboardHelp } from "../components/help/help-sections/dashboard-help"
import { ExpensesHelp } from "../components/help/help-sections/expenses-help"
import { PartnersHelp } from "../components/help/help-sections/partners-help"
import { SettingsHelp } from "../components/help/help-sections/settings-help"

const VALID_SECTIONS = ["dashboard", "expenses", "categories", "partners", "settings"]

function getNavItems(t: (key: string) => string): InnerSideNavItem[] {
  return [
    {
      id: "dashboard",
      title: t("pages.help.tabs.dashboard"),
      icon: LayoutDashboard,
      anchors: [
        { id: "dashboard-header-actions", title: t("pages.help.dashboard.headerActions.title") },
        { id: "dashboard-global-summary", title: t("pages.help.dashboard.globalSummary.title") },
        { id: "dashboard-sankey", title: t("pages.help.dashboard.sankey.title") },
        {
          id: "dashboard-monthly-expenses",
          title: t("pages.help.dashboard.monthlyExpenses.title"),
        },
        { id: "dashboard-expense-sources", title: t("pages.help.dashboard.expenseSources.title") },
        { id: "dashboard-monthly-balance", title: t("pages.help.dashboard.monthlyBalance.title") },
        {
          id: "dashboard-partner-contribution",
          title: t("pages.help.dashboard.partnerContribution.title"),
        },
        { id: "dashboard-top-expenses", title: t("pages.help.dashboard.topExpenses.title") },
        {
          id: "dashboard-category-comparison",
          title: t("pages.help.dashboard.categoryComparison.title"),
        },
      ],
    },
    {
      id: "expenses",
      title: t("pages.help.tabs.expenses"),
      icon: CreditCard,
      anchors: [
        { id: "expenses-table", title: t("pages.help.expenses.table.title") },
        { id: "expenses-actions", title: t("pages.help.expenses.actions.title") },
        { id: "expenses-form", title: t("pages.help.expenses.form.title") },
        { id: "expenses-filters", title: t("pages.help.expenses.filters.title") },
        { id: "expenses-csv-import", title: t("pages.help.expenses.csvImport.title") },
        { id: "expenses-permissions", title: t("pages.help.expenses.permissions.title") },
      ],
    },
    {
      id: "categories",
      title: t("pages.help.tabs.categories"),
      icon: Tag,
      anchors: [
        { id: "categories-table", title: t("pages.help.categories.table.title") },
        { id: "categories-actions", title: t("pages.help.categories.actions.title") },
        { id: "categories-form", title: t("pages.help.categories.form.title") },
        { id: "categories-permissions", title: t("pages.help.categories.permissions.title") },
      ],
    },
    {
      id: "partners",
      title: t("pages.help.tabs.partners"),
      icon: UserRound,
      anchors: [
        { id: "partners-table", title: t("pages.help.partners.table.title") },
        { id: "partners-actions", title: t("pages.help.partners.actions.title") },
        { id: "partners-form", title: t("pages.help.partners.form.title") },
        { id: "partners-limits", title: t("pages.help.partners.limits.title") },
        { id: "partners-privacy", title: t("pages.help.partners.privacy.title") },
        { id: "partners-permissions", title: t("pages.help.partners.permissions.title") },
      ],
    },
    {
      id: "settings",
      title: t("pages.help.tabs.settings"),
      icon: Settings,
      anchors: [
        { id: "settings-general", title: t("pages.help.settings.general.title") },
        { id: "settings-invitations", title: t("pages.help.settings.invitations.title") },
        { id: "settings-data", title: t("pages.help.settings.data.title") },
      ],
    },
  ]
}

export function HelpSection() {
  const { section } = useParams<{ section: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useI18n()

  useEffect(() => {
    if (section && !VALID_SECTIONS.includes(section)) {
      navigate("/help", { replace: true })
    }
  }, [section, navigate])

  const activeSection = section && VALID_SECTIONS.includes(section) ? section : "dashboard"
  const activeAnchorId = location.hash ? location.hash.substring(1) : undefined

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1)
      scrollToAnchor(id)
    }
  }, [location.hash, activeSection])

  const navItems = getNavItems(t)

  const handleSectionChange = (id: string) => {
    navigate(`/help/${id}`)
  }

  const handleBackToOverview = () => {
    navigate("/help")
  }

  const handleAnchorClick = (anchorId: string) => {
    const newUrl = `/help/${activeSection}#${anchorId}`
    navigate(newUrl, { replace: true })
    scrollToAnchor(anchorId)
  }

  const getSectionTitle = (sectionId: string) => {
    return navItems.find((item) => item.id === sectionId)?.title ?? sectionId
  }

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return <DashboardHelp />
      case "expenses":
        return <ExpensesHelp />
      case "categories":
        return <CategoriesHelp />
      case "partners":
        return <PartnersHelp />
      case "settings":
        return <SettingsHelp />
      default:
        return <DashboardHelp />
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6">
      <motion.div initial="hidden" animate="visible" variants={slideUp} className="flex-shrink-0">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/help">{t("pages.help.title")}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{getSectionTitle(activeSection)}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </motion.div>

      <div className="flex flex-col md:flex-row gap-6 items-start flex-1 min-h-0">
        <div className="flex-shrink-0 self-start">
          <InnerSideNav
            items={navItems}
            activeId={activeSection}
            activeAnchorId={activeAnchorId}
            onItemClick={handleSectionChange}
            sheetTitle={t("pages.help.sheetTitle")}
            sheetDescription={t("pages.help.sheetDescription")}
            showBackToOverview={true}
            onBackToOverview={handleBackToOverview}
            backToOverviewLabel={t("pages.help.backToOverview")}
            onAnchorClick={handleAnchorClick}
          />
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={slideUp}
          className="flex-1 min-w-0 w-full"
        >
          <Card className="border-2">
            <CardContent className="p-6">{renderContent()}</CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
